import { useState, useCallback } from "react";
import { useMutation } from "@apollo/client";
import { UPDATE_PERSON_PROFILE_PHOTO_MUTATION } from "./mutations";
import { showSnackbar } from "@/lib/snackbarUtils";
import useAmplitude from "@/hooks/useAmplitude";
import {
  PHOTOBOOTH_ANALYTICS_EVENTS,
  getPhotoboothPhotoSavedAnalyticsData,
  type PhotoboothAnalyticsSource,
  type PhotoboothCaptureMethod
} from "./_utils/amplitude";

interface UsePhotoboothUploadProps {
  personId: string;
  personPgaId: string;
  analyticsSource: PhotoboothAnalyticsSource;
  captureMethod?: PhotoboothCaptureMethod;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

interface UsePhotoboothUploadResult {
  upload: (imageData: string) => Promise<void>;
  isUploading: boolean;
  error: Error | null;
}

type PhotoboothUploadErrorCode =
  | "configuration"
  | "prepare-image"
  | "network"
  | "cloudinary-upload"
  | "profile-update"
  | "unknown";

class PhotoboothUploadError extends Error {
  constructor(
    public readonly code: PhotoboothUploadErrorCode,
    message = getPhotoboothUploadErrorMessage(code)
  ) {
    super(message);
    this.name = "PhotoboothUploadError";
  }
}

function getPhotoboothUploadErrorMessage(code: PhotoboothUploadErrorCode): string {
  switch (code) {
    case "configuration":
      return "Photo uploads are temporarily unavailable. Please try again later.";
    case "prepare-image":
      return "We couldn't prepare this photo for upload. Please choose a different image and try again.";
    case "network":
      return "Your connection was interrupted while saving the photo. Please check your internet connection and try again.";
    case "cloudinary-upload":
      return "We couldn't upload the photo right now. Please try again in a moment.";
    case "profile-update":
      return "The photo uploaded, but we couldn't save it to the profile. Please try again.";
    default:
      return "We couldn't save this photo right now. Please try again.";
  }
}

function normalizePhotoboothUploadError(error: unknown): PhotoboothUploadError {
  if (error instanceof PhotoboothUploadError) {
    return error;
  }

  if (error instanceof TypeError) {
    return new PhotoboothUploadError("network");
  }

  return new PhotoboothUploadError("unknown");
}

/**
 * Hook for uploading photos from the Photobooth feature.
 * Converts base64 image data to a blob, uploads to Cloudinary,
 * then updates the person's profile photo via GraphQL mutation.
 */
export function usePhotoboothUpload({
  personId,
  personPgaId,
  analyticsSource,
  captureMethod,
  onSuccess,
  onError
}: UsePhotoboothUploadProps): UsePhotoboothUploadResult {
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [updatePersonMutation] = useMutation(UPDATE_PERSON_PROFILE_PHOTO_MUTATION);
  const { trackEvent } = useAmplitude();

  const uploadToCloudinary = useCallback(
    async (base64Data: string): Promise<string> => {
      const cloudName = import.meta.env.VITE_CLOUDINARY_CLOUDNAME;
      const profilePhotoUploadPreset = import.meta.env.VITE_CLOUDINARY_UPLOAD_PRESET;
      const memberCardUploadPreset = import.meta.env.VITE_CLOUDINARY_MEMBER_CARD_UPLOAD_PRESET;

      if (!cloudName || !profilePhotoUploadPreset || !memberCardUploadPreset) {
        throw new PhotoboothUploadError("configuration");
      }

      let blob: Blob;
      try {
        const base64Response = await fetch(base64Data);
        blob = await base64Response.blob();
      } catch {
        throw new PhotoboothUploadError("prepare-image");
      }

      const uploadUrl = `https://api.cloudinary.com/v1_1/${cloudName}/image/upload`;

      const uploadBlob = async (
        uploadPreset: string,
        options?: { publicId?: string; folder?: string }
      ): Promise<{ secure_url: string }> => {
        const formData = new FormData();
        formData.append("file", blob);
        formData.append("upload_preset", uploadPreset);
        if (options?.folder) {
          formData.append("folder", options.folder);
        }
        if (options?.publicId) {
          formData.append("public_id", options.publicId);
        }
        // Unsigned uploads cannot use `overwrite` or `invalidate`; configure replacement in the upload preset if needed.

        let response: Response;
        try {
          response = await fetch(uploadUrl, { method: "POST", body: formData });
        } catch {
          throw new PhotoboothUploadError("network");
        }

        if (!response.ok) {
          throw new PhotoboothUploadError("cloudinary-upload");
        }

        const data = await response.json();
        if (!data?.secure_url) {
          throw new PhotoboothUploadError("cloudinary-upload");
        }
        return data;
      };

      const [profileData] = await Promise.all([
        uploadBlob(profilePhotoUploadPreset, { folder: "profile-photos" }),
        uploadBlob(memberCardUploadPreset, { publicId: personPgaId })
      ]);

      return profileData.secure_url;
    },
    [personPgaId]
  );

  const upload = useCallback(
    async (imageData: string) => {
      setIsUploading(true);
      setError(null);

      try {
        // Upload to Cloudinary
        const photoUrl = await uploadToCloudinary(imageData);

        // Update person's profile photo via mutation
        let data;
        try {
          ({ data } = await updatePersonMutation({
            variables: {
              input: {
                id: personId,
                profilePhotoUrl: photoUrl
              }
            }
          }));
        } catch {
          throw new PhotoboothUploadError("profile-update");
        }

        if (data?.updatePerson?.success) {
          trackEvent(
            PHOTOBOOTH_ANALYTICS_EVENTS.photoSaved,
            getPhotoboothPhotoSavedAnalyticsData({
              pgaId: personPgaId,
              source: analyticsSource,
              captureMethod
            })
          );
          showSnackbar("Profile photo updated successfully");
          onSuccess?.();
        } else {
          throw new PhotoboothUploadError("profile-update");
        }
      } catch (err) {
        const uploadError = normalizePhotoboothUploadError(err);
        console.error("Photobooth upload failed", err);
        setError(uploadError);
        onError?.(uploadError);
        throw uploadError;
      } finally {
        setIsUploading(false);
      }
    },
    [
      personId,
      personPgaId,
      analyticsSource,
      captureMethod,
      uploadToCloudinary,
      updatePersonMutation,
      trackEvent,
      onSuccess,
      onError
    ]
  );

  return {
    upload,
    isUploading,
    error
  };
}
